import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiFetch, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/app/$propertyId/zones')({
  component: PropertyZones,
});

type ZoneSummary = {
  id: string;
  characterId: string;
  name: string;
  displayOrder: number | null;
  targetCadenceDays: number | null;
  effectiveCadenceDays: number;
  colorState: 'green' | 'amber' | 'red' | 'neutral';
  lastCompletedAt: string | null;
  deferredUntil: string | null;
  archivedAt: string | null;
};

type Equipment = { id: string; name: string; archivedAt: string | null };

type PropertyDetail = { role: 'owner' | 'member' | null };

const COLOR_DOT: Record<ZoneSummary['colorState'], string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-400',
  red: 'bg-red-500',
  neutral: 'bg-gray-300',
};

// ---------------------------------------------------------------------------
// Log completion modal
// ---------------------------------------------------------------------------

type LogCompletionModalProps = {
  propertyId: string;
  zones: ZoneSummary[];
  preselectedZoneId: string | null;
  onClose: () => void;
};

function LogCompletionModal({ propertyId, zones, preselectedZoneId, onClose }: LogCompletionModalProps) {
  const { data: equipment, isPending: equipPending } = useQuery({
    queryKey: ['equipment', propertyId],
    queryFn: () =>
      api
        .get<{ equipment: Equipment[] }>('/v1/properties/' + propertyId + '/equipment')
        .then((r) => r.equipment),
  });
  const activeEquipment = equipment?.filter((e) => !e.archivedAt) ?? [];

  const [workedOn, setWorkedOn] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [equipmentId, setEquipmentId] = useState('');
  const [selectedZoneIds, setSelectedZoneIds] = useState<Set<string>>(
    () => new Set(preselectedZoneId ? [preselectedZoneId] : []),
  );
  const [error, setError] = useState<string | null>(null);

  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: () =>
      apiFetch<{ completions: unknown[] }>('/v1/properties/' + propertyId + '/zone-completions', {
        method: 'POST',
        body: JSON.stringify({
          zoneIds: [...selectedZoneIds],
          equipmentId,
          workedOn: new Date(workedOn + 'T12:00:00').toISOString(),
        }),
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': crypto.randomUUID(),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zones', propertyId] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-background rounded-lg border border-border shadow-lg w-full max-w-md mx-4 p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Log completion</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">×</button>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Date</label>
          <input
            type="date"
            value={workedOn}
            onChange={(e) => setWorkedOn(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Equipment</label>
          {equipPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <select
              value={equipmentId}
              onChange={(e) => setEquipmentId(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select equipment…</option>
              {activeEquipment.map((eq) => (
                <option key={eq.id} value={eq.id}>{eq.name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Zones</label>
          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto rounded-md border border-input p-2">
            {zones.map((zone) => (
              <label key={zone.id} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                <input
                  type="checkbox"
                  checked={selectedZoneIds.has(zone.id)}
                  onChange={(e) => {
                    setSelectedZoneIds((prev) => {
                      const next = new Set(prev);
                      e.target.checked ? next.add(zone.id) : next.delete(zone.id);
                      return next;
                    });
                  }}
                />
                <span className="font-mono text-xs bg-muted px-1 rounded text-muted-foreground">{zone.characterId}</span>
                <span>{zone.name}</span>
              </label>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => submit.mutate()}
            disabled={!equipmentId || selectedZoneIds.size === 0 || submit.isPending}
          >
            {submit.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Zone edit modal (owner only)
// ---------------------------------------------------------------------------

type ZoneEditModalProps = {
  zone: ZoneSummary;
  onClose: () => void;
};

function ZoneEditModal({ zone, onClose }: ZoneEditModalProps) {
  const qc = useQueryClient();
  const [characterId, setCharacterId] = useState(zone.characterId);
  const [name, setName] = useState(zone.name);
  const [cadence, setCadence] = useState(zone.targetCadenceDays ? String(zone.targetCadenceDays) : '');
  const [error, setError] = useState<string | null>(null);

  const editMutation = useMutation({
    mutationFn: () =>
      api.patch('/v1/zones/' + zone.id, {
        characterId: characterId.trim(),
        name: name.trim(),
        ...(cadence ? { targetCadenceDays: parseInt(cadence, 10) } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zones'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const msgs: Record<string, string> = {
          character_id_taken: 'That character ID is already used by another zone.',
          name_taken: 'That name is already used by another zone.',
        };
        setError(msgs[err.code ?? ''] ?? err.message ?? 'Save failed.');
      } else {
        setError('Save failed.');
      }
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-background rounded-lg border border-border shadow-lg w-full max-w-sm mx-4 p-5 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Edit zone</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">×</button>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Character ID</label>
          <input
            type="text"
            value={characterId}
            onChange={(e) => setCharacterId(e.target.value)}
            className="font-mono rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Cadence override (days, leave blank to use property default)</label>
          <input
            type="number"
            min={1}
            max={365}
            value={cadence}
            onChange={(e) => setCadence(e.target.value)}
            placeholder="Use property default"
            className="w-48 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={() => editMutation.mutate()}
            disabled={!characterId.trim() || !name.trim() || editMutation.isPending}
          >
            {editMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function PropertyZones() {
  const { propertyId } = Route.useParams();
  const qc = useQueryClient();
  const [logOpen, setLogOpen] = useState(false);
  const [preselectedZoneId, setPreselectedZoneId] = useState<string | null>(null);
  const [editingZone, setEditingZone] = useState<ZoneSummary | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [deferringZoneId, setDeferringZoneId] = useState<string | null>(null);
  const [deferDate, setDeferDate] = useState('');

  const { data: propData } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () =>
      api.get<{ property: PropertyDetail }>('/v1/properties/' + propertyId),
  });
  const isOwner = propData?.property?.role === 'owner';

  const { data: zones, isPending, error } = useQuery({
    queryKey: ['zones', propertyId],
    queryFn: () =>
      api.get<ZoneSummary[]>('/v1/properties/' + propertyId + '/zones?includeArchived=true'),
  });

  const archiveZoneMutation = useMutation({
    mutationFn: ({ id, archive }: { id: string; archive: boolean }) =>
      api.post('/v1/zones/' + id + (archive ? '/archive' : '/unarchive'), {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['zones', propertyId] }),
  });

  const deferMutation = useMutation({
    mutationFn: ({ id, until }: { id: string; until: string }) =>
      api.post('/v1/zones/' + id + '/defer', { deferredUntil: new Date(until + 'T12:00:00').toISOString() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zones', propertyId] });
      setDeferringZoneId(null);
      setDeferDate('');
    },
  });

  const clearDeferMutation = useMutation({
    mutationFn: (id: string) => api.delete('/v1/zones/' + id + '/defer'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['zones', propertyId] }),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ characterId: '', name: '', cadence: '' });
  const [createError, setCreateError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      api.post('/v1/properties/' + propertyId + '/zones', {
        characterId: createForm.characterId.trim(),
        name: createForm.name.trim(),
        ...(createForm.cadence ? { targetCadenceDays: parseInt(createForm.cadence, 10) } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zones', propertyId] });
      setShowCreate(false);
      setCreateForm({ characterId: '', name: '', cadence: '' });
      setCreateError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const msgs: Record<string, string> = {
          character_id_taken: 'That character ID is already in use.',
          name_taken: 'That zone name is already in use.',
        };
        setCreateError(msgs[err.code ?? ''] ?? err.message ?? 'Create failed.');
      } else {
        setCreateError('Create failed.');
      }
    },
  });

  const active = zones?.filter((z) => !z.archivedAt) ?? [];
  const archived = zones?.filter((z) => z.archivedAt) ?? [];

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Zones</h2>
          {archived.length > 0 && (
            <label className="flex items-center gap-1.5 text-xs cursor-pointer text-muted-foreground">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isOwner && !showCreate && (
            <Button size="sm" variant="outline" onClick={() => setShowCreate(true)}>
              Add zone
            </Button>
          )}
          <Button size="sm" onClick={() => { setPreselectedZoneId(null); setLogOpen(true); }}>
            Log completion
          </Button>
        </div>
      </div>

      {isOwner && showCreate && (
        <div className="rounded-lg border border-border p-4 mb-4 flex flex-col gap-3">
          <h3 className="text-sm font-medium">New zone</h3>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="ID (e.g. A1)"
              value={createForm.characterId}
              onChange={(e) => setCreateForm((f) => ({ ...f, characterId: e.target.value }))}
              className="w-24 font-mono rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              type="text"
              placeholder="Name"
              value={createForm.name}
              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              type="number"
              min={1}
              max={365}
              placeholder="Cadence (days)"
              value={createForm.cadence}
              onChange={(e) => setCreateForm((f) => ({ ...f, cadence: e.target.value }))}
              className="w-36 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          {createError && <p className="text-sm text-destructive">{createError}</p>}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => createMutation.mutate()}
              disabled={!createForm.characterId.trim() || !createForm.name.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating…' : 'Create'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowCreate(false); setCreateError(null); }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {isPending && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">Failed to load zones.</p>}

      {!isPending && !error && (
        <div className="flex flex-col gap-2">
          {active.length === 0 && (
            <p className="text-muted-foreground text-sm">No zones yet.</p>
          )}
          {active.map((zone) => {
            const isActiveDefer = zone.deferredUntil && new Date(zone.deferredUntil) > new Date();
            const isDeferring = deferringZoneId === zone.id;
            return (
              <div
                key={zone.id}
                className="flex flex-col rounded-lg border border-border bg-card px-4 py-3 gap-2"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                    {zone.characterId}
                  </span>
                  <span className="flex-1 font-medium">{zone.name}</span>
                  <div className={cn('w-3 h-3 rounded-full shrink-0', COLOR_DOT[zone.colorState])} />
                  <span className="text-xs text-muted-foreground">
                    {zone.lastCompletedAt
                      ? new Date(zone.lastCompletedAt.slice(0, 10) + 'T12:00:00').toLocaleDateString()
                      : 'Never'}
                  </span>
                  {isOwner && (
                    <>
                      <button
                        className="text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => setEditingZone(zone)}
                      >
                        Edit
                      </button>
                      <button
                        className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                        onClick={() => archiveZoneMutation.mutate({ id: zone.id, archive: true })}
                        disabled={archiveZoneMutation.isPending}
                      >
                        Archive
                      </button>
                    </>
                  )}
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => { setPreselectedZoneId(zone.id); setLogOpen(true); }}
                  >
                    Log
                  </button>
                  {isOwner && (
                    <button
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        if (isDeferring) {
                          setDeferringZoneId(null);
                          setDeferDate('');
                        } else {
                          setDeferringZoneId(zone.id);
                          setDeferDate('');
                        }
                      }}
                    >
                      Defer
                    </button>
                  )}
                </div>

                {isActiveDefer && !isDeferring && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Deferred until {new Date(zone.deferredUntil!.slice(0, 10) + 'T12:00:00').toLocaleDateString()}</span>
                    {isOwner && (
                      <button
                        className="hover:text-foreground disabled:opacity-50"
                        onClick={() => clearDeferMutation.mutate(zone.id)}
                        disabled={clearDeferMutation.isPending}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}

                {isDeferring && (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={deferDate}
                      min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                      onChange={(e) => setDeferDate(e.target.value)}
                      className="rounded-md border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <Button
                      size="sm"
                      className="h-6 text-xs px-2"
                      disabled={!deferDate || deferMutation.isPending}
                      onClick={() => deferMutation.mutate({ id: zone.id, until: deferDate })}
                    >
                      {deferMutation.isPending ? 'Saving…' : 'Set'}
                    </Button>
                    <button
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => { setDeferringZoneId(null); setDeferDate(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {showArchived && archived.length > 0 && (
            <>
              <div className="text-xs text-muted-foreground mt-2 mb-1">Archived zones</div>
              {archived.map((zone) => (
                <div
                  key={zone.id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 opacity-60"
                >
                  <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                    {zone.characterId}
                  </span>
                  <span className="flex-1 text-muted-foreground">{zone.name}</span>
                  <span className="text-xs text-muted-foreground">Archived</span>
                  {isOwner && (
                    <button
                      className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                      onClick={() => archiveZoneMutation.mutate({ id: zone.id, archive: false })}
                      disabled={archiveZoneMutation.isPending}
                    >
                      Unarchive
                    </button>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {logOpen && (
        <LogCompletionModal
          propertyId={propertyId}
          zones={active}
          preselectedZoneId={preselectedZoneId}
          onClose={() => setLogOpen(false)}
        />
      )}

      {editingZone && (
        <ZoneEditModal
          zone={editingZone}
          onClose={() => setEditingZone(null)}
        />
      )}
    </div>
  );
}
